import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Calendar, Clock, Repeat, CheckCircle, XCircle, AlertCircle, Plus, Eye } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { formatCurrency } from "@/lib/currency";
import { format } from "date-fns";

interface RecurringBooking {
  id: string;
  booking_number: string;
  customer_id: string;
  company_id: string;
  service_id: string;
  scheduled_date: string;
  scheduled_time: string;
  status: string;
  total_amount: number;
  series_id: string;
  parent_booking_id: string | null;
  is_recurring_parent: boolean;
  recurring_occurrences: number;
  recurrence_frequency: string;
  recurrence_end_date: string;
  service_name: string;
  company_name: string;
  customer_name: string;
  completed_occurrences: number;
  is_series_completed: boolean;
}

interface SubBooking {
  id: string;
  booking_number: string;
  scheduled_date: string;
  scheduled_time: string;
  status: string;
  total_amount: number;
}

interface RecurringBookingManagerProps {
  companyId: string;
}

export default function RecurringBookingManager({ companyId }: RecurringBookingManagerProps) {
  const [recurringBookings, setRecurringBookings] = useState<RecurringBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState<RecurringBooking | null>(null);
  const [subBookings, setSubBookings] = useState<SubBooking[]>([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const { toast } = useToast();

  const formatTimeHHmm = (t?: string) => {
    if (!t) return '';
    const s = String(t);
    if (/^\d{2}:\d{2}(:\d{2})?$/.test(s)) return s.slice(0, 5);
    return s;
  };

  // Form state for creating recurring booking
  const [createForm, setCreateForm] = useState({
    customer_id: "",
    service_id: "",
    service_address_id: "",
    scheduled_date: "",
    scheduled_time: "",
    total_amount: "",
    customer_fee_cents: "",
    recurrence_frequency: "weekly",
    recurrence_end_date: "",
    notes: ""
  });

  useEffect(() => {
    loadRecurringBookings();
  }, [companyId]);

  const loadRecurringBookings = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('recurring_bookings_view')
        .select('*')
        .eq('company_id', companyId)
        .order('scheduled_date', { ascending: false });

      if (error) throw error;
      setRecurringBookings(data || []);
    } catch (error: any) {
      console.error('Error loading recurring bookings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load recurring bookings',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadSubBookings = async (bookingId: string) => {
    try {
      // Use the dedicated sub-bookings RPC (details function was replaced in migration)
      const { data, error } = await supabase
        .rpc('get_recurring_booking_sub_bookings', { p_booking_id: bookingId });

      if (error) throw error;
      
      // Map returned rows directly (RPC already excludes parent)
      const subBookings = (data || []).map((item: any) => ({
        id: item.booking_id,
        booking_number: item.booking_number,
        scheduled_date: item.scheduled_date,
        scheduled_time: item.scheduled_time,
        status: item.status,
        total_amount: item.total_amount
      }));
      
      setSubBookings(subBookings);
    } catch (error: any) {
      console.error('Error loading sub-bookings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load booking details',
        variant: 'destructive'
      });
    }
  };

  const handleCreateRecurring = async () => {
    try {
      const { data, error } = await supabase.rpc('create_recurring_booking', {
        p_customer_id: createForm.customer_id,
        p_company_id: companyId,
        p_service_id: createForm.service_id,
        p_service_address_id: createForm.service_address_id,
        p_scheduled_date: createForm.scheduled_date,
        p_scheduled_time: createForm.scheduled_time,
        p_total_amount: parseFloat(createForm.total_amount),
        p_customer_fee_cents: parseInt(createForm.customer_fee_cents) || 0,
        p_recurrence_frequency: createForm.recurrence_frequency,
        p_recurrence_end_date: createForm.recurrence_end_date,
        p_notes: createForm.notes || null
      });

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Recurring booking series created successfully'
      });

      setShowCreateDialog(false);
      setCreateForm({
        customer_id: "",
        service_id: "",
        service_address_id: "",
        scheduled_date: "",
        scheduled_time: "",
        total_amount: "",
        customer_fee_cents: "",
        recurrence_frequency: "weekly",
        recurrence_end_date: "",
        notes: ""
      });

      loadRecurringBookings();
    } catch (error: any) {
      console.error('Error creating recurring booking:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create recurring booking',
        variant: 'destructive'
      });
    }
  };

  const handleCompleteOccurrence = async (bookingId: string) => {
    try {
      const { data, error } = await supabase.rpc('complete_recurring_occurrence', {
        p_booking_id: bookingId
      });

      if (error) throw error;

      const isSeriesComplete = data;
      
      toast({
        title: 'Success',
        description: isSeriesComplete 
          ? 'Occurrence completed and entire series is now complete!'
          : 'Occurrence completed successfully'
      });

      loadRecurringBookings();
      if (selectedBooking) {
        loadSubBookings(selectedBooking.id);
      }
    } catch (error: any) {
      console.error('Error completing occurrence:', error);
      toast({
        title: 'Error',
        description: 'Failed to complete occurrence',
        variant: 'destructive'
      });
    }
  };

  const handleCancelSeries = async (bookingId: string) => {
    try {
      const { data, error } = await supabase.rpc('cancel_recurring_series', {
        p_booking_id: bookingId,
        p_reason: 'series_cancelled'
      });

      if (error) throw error;

      const cancelledCount = data;
      
      toast({
        title: 'Success',
        description: `Cancelled ${cancelledCount} bookings in the series`
      });

      loadRecurringBookings();
    } catch (error: any) {
      console.error('Error cancelling series:', error);
      toast({
        title: 'Error',
        description: 'Failed to cancel series',
        variant: 'destructive'
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge variant="default" className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Completed</Badge>;
      case 'cancelled':
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Cancelled</Badge>;
      case 'confirmed':
        return <Badge variant="default" className="bg-blue-100 text-blue-800"><Clock className="h-3 w-3 mr-1" />Confirmed</Badge>;
      default:
        return <Badge variant="secondary"><AlertCircle className="h-3 w-3 mr-1" />Pending</Badge>;
    }
  };

  const getFrequencyLabel = (frequency: string) => {
    switch (frequency) {
      case 'weekly': return 'Weekly';
      case 'biweekly': return 'Bi-weekly';
      case 'monthly': return 'Monthly';
      default: return frequency;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-muted-foreground">Loading recurring bookings...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6 w-full overflow-hidden">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold break-words">Recurring Bookings</h2>
          <p className="text-muted-foreground break-words">Manage recurring booking series and occurrences</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Create Recurring Series
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create Recurring Booking Series</DialogTitle>
              <DialogDescription>
                Create a series of recurring bookings for a customer
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="customer_id">Customer ID</Label>
                <Input
                  id="customer_id"
                  value={createForm.customer_id}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, customer_id: e.target.value }))}
                  placeholder="Customer UUID"
                />
              </div>
              <div>
                <Label htmlFor="service_id">Service ID</Label>
                <Input
                  id="service_id"
                  value={createForm.service_id}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, service_id: e.target.value }))}
                  placeholder="Service UUID"
                />
              </div>
              <div>
                <Label htmlFor="scheduled_date">Start Date</Label>
                <Input
                  id="scheduled_date"
                  type="date"
                  value={createForm.scheduled_date}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, scheduled_date: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="scheduled_time">Start Time</Label>
                <Input
                  id="scheduled_time"
                  type="time"
                  value={createForm.scheduled_time}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, scheduled_time: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="total_amount">Total Amount</Label>
                <Input
                  id="total_amount"
                  type="number"
                  step="0.01"
                  value={createForm.total_amount}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, total_amount: e.target.value }))}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label htmlFor="recurrence_frequency">Frequency</Label>
                <Select value={createForm.recurrence_frequency} onValueChange={(value) => setCreateForm(prev => ({ ...prev, recurrence_frequency: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="biweekly">Bi-weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label htmlFor="recurrence_end_date">End Date</Label>
                <Input
                  id="recurrence_end_date"
                  type="date"
                  value={createForm.recurrence_end_date}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, recurrence_end_date: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={createForm.notes}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Optional notes for the booking series"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateRecurring}>
                Create Series
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="active" className="w-full min-w-0">
        <TabsList className="w-full flex flex-wrap gap-2">
          <TabsTrigger value="active">Active Series</TabsTrigger>
          <TabsTrigger value="completed">Completed Series</TabsTrigger>
          <TabsTrigger value="cancelled">Cancelled Series</TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="space-y-4">
          {recurringBookings.filter(b => !b.is_series_completed && b.status !== 'cancelled').map((booking) => (
            <Card key={booking.id}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Repeat className="h-4 w-4 text-blue-500" />
                      <span className="font-semibold">{booking.booking_number}</span>
                      {getStatusBadge(booking.status)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      <div>{booking.customer_name} • {booking.service_name}</div>
                      <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {format(new Date(booking.scheduled_date), 'MMM d, yyyy')}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {booking.scheduled_time}
                        </span>
                        <span className="flex items-center gap-1">
                          <Repeat className="h-3 w-3" />
                          {getFrequencyLabel(booking.recurrence_frequency)}
                        </span>
                      </div>
                    </div>
                    <div className="text-sm">
                      <span className="font-medium">{formatCurrency(booking.total_amount)}</span>
                      <span className="text-muted-foreground ml-2">
                        • {booking.completed_occurrences}/{booking.recurring_occurrences} completed
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Dialog open={showDetailsDialog && selectedBooking?.id === booking.id} onOpenChange={(open) => {
                      setShowDetailsDialog(open);
                      if (open) {
                        setSelectedBooking(booking);
                        loadSubBookings(booking.id);
                      }
                    }}>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm">
                          <Eye className="h-4 w-4 mr-1" />
                          View Details
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-4xl">
                        <DialogHeader>
                          <DialogTitle>Recurring Booking Details</DialogTitle>
                          <DialogDescription>
                            {booking.booking_number} - {booking.customer_name}
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label>Service</Label>
                              <p className="text-sm">{booking.service_name}</p>
                            </div>
                            <div>
                              <Label>Frequency</Label>
                              <p className="text-sm">{getFrequencyLabel(booking.recurrence_frequency)}</p>
                            </div>
                            <div>
                              <Label>Total Amount</Label>
                              <p className="text-sm font-medium">{formatCurrency(booking.total_amount)}</p>
                            </div>
                            <div>
                              <Label>Progress</Label>
                              <p className="text-sm">{booking.completed_occurrences}/{booking.recurring_occurrences} completed</p>
                            </div>
                          </div>
                          
                          <div>
                            <Label>All Occurrences</Label>
                            <div className="mt-2 space-y-2 max-h-60 overflow-y-auto">
                              {subBookings.map((sub) => (
                                <div key={sub.id} className="flex items-center justify-between p-3 border rounded">
                                  <div className="flex items-center gap-3">
                                    {getStatusBadge(sub.status)}
                                    <div>
                                      <div className="font-medium">{sub.booking_number}</div>
                                      <div className="text-sm text-muted-foreground">
                                        {format(new Date(sub.scheduled_date), 'MMM d, yyyy')} at {formatTimeHHmm(sub.scheduled_time)}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium">{formatCurrency(sub.total_amount)}</span>
                                    {sub.status === 'pending' && (
                                      <Button 
                                        size="sm" 
                                        onClick={() => handleCompleteOccurrence(sub.id)}
                                      >
                                        Complete
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={() => handleCancelSeries(booking.id)}
                    >
                      Cancel Series
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          
          {recurringBookings.filter(b => !b.is_series_completed && b.status !== 'cancelled').length === 0 && (
            <Card>
              <CardContent className="py-8 text-center">
                <Repeat className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No Active Recurring Series</h3>
                <p className="text-muted-foreground">
                  Create your first recurring booking series to get started.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="completed" className="space-y-4">
          {recurringBookings.filter(b => b.is_series_completed).map((booking) => (
            <Card key={booking.id}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Repeat className="h-4 w-4 text-green-500" />
                      <span className="font-semibold">{booking.booking_number}</span>
                      <Badge variant="default" className="bg-green-100 text-green-800">
                        <CheckCircle className="h-3 w-3 mr-1" />Series Complete
                      </Badge>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      <div>{booking.customer_name} • {booking.service_name}</div>
                      <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {format(new Date(booking.scheduled_date), 'MMM d, yyyy')}
                        </span>
                        <span className="flex items-center gap-1">
                          <Repeat className="h-3 w-3" />
                          {getFrequencyLabel(booking.recurrence_frequency)}
                        </span>
                      </div>
                    </div>
                    <div className="text-sm">
                      <span className="font-medium">{formatCurrency(booking.total_amount)}</span>
                      <span className="text-muted-foreground ml-2">
                        • {booking.completed_occurrences}/{booking.recurring_occurrences} completed
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          
          {recurringBookings.filter(b => b.is_series_completed).length === 0 && (
            <Card>
              <CardContent className="py-8 text-center">
                <CheckCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No Completed Series</h3>
                <p className="text-muted-foreground">
                  Completed recurring booking series will appear here.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="cancelled" className="space-y-4">
          {recurringBookings.filter(b => b.status === 'cancelled').map((booking) => (
            <Card key={booking.id}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Repeat className="h-4 w-4 text-red-500" />
                      <span className="font-semibold">{booking.booking_number}</span>
                      <Badge variant="destructive">
                        <XCircle className="h-3 w-3 mr-1" />Cancelled
                      </Badge>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      <div>{booking.customer_name} • {booking.service_name}</div>
                      <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {format(new Date(booking.scheduled_date), 'MMM d, yyyy')}
                        </span>
                        <span className="flex items-center gap-1">
                          <Repeat className="h-3 w-3" />
                          {getFrequencyLabel(booking.recurrence_frequency)}
                        </span>
                      </div>
                    </div>
                    <div className="text-sm">
                      <span className="font-medium">{formatCurrency(booking.total_amount)}</span>
                      <span className="text-muted-foreground ml-2">
                        • {booking.completed_occurrences}/{booking.recurring_occurrences} completed
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          
          {recurringBookings.filter(b => b.status === 'cancelled').length === 0 && (
            <Card>
              <CardContent className="py-8 text-center">
                <XCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No Cancelled Series</h3>
                <p className="text-muted-foreground">
                  Cancelled recurring booking series will appear here.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
